import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const r2 = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Main Admin only: staff commissions requested by Secondary Admin wait here for GIVE PERMISSION.
export default function StaffCommissionApprovals({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/commissions/staff/pending").then(({ data }) => {
    setRows(data);
    setRates(Object.fromEntries(data.map((o) => [o.order_id, o.lines.map((l) => l.auto_rate)])));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (o, approve) => {
    setBusy(o.order_id);
    try {
      if (approve) await api.post(`/orders/${o.order_id}/staff-commission/approve`, { rates: rates[o.order_id].map((v) => Number(v) || 0) });
      else await api.post(`/orders/${o.order_id}/staff-commission/reject`);
      toast.success(approve ? `Commission approved · #${o.order_number}` : `Rejected · #${o.order_number}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="surface p-4 rounded-sm border-l-4 border-amber-500 space-y-3" data-testid="staff-commission-approvals">
      <div className="font-heading text-sm font-black tracking-wide text-amber-300">STAFF COMMISSION · PENDING APPROVAL ({rows.length})</div>
      {rows.length === 0 && <div className="text-xs text-slate-500" data-testid="staff-approvals-empty">Koi pending request nahi.</div>}
      {rows.map((o) => {
        const total = o.lines.reduce((t, l, i) => t + (Number(rates[o.order_id]?.[i]) || 0) * l.sqft, 0);
        return (
          <div key={o.order_id} className="surface-2 p-3 rounded-sm space-y-2" data-testid={`approval-row-${o.order_number}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <b className="font-mono-num text-white">#{o.order_number}</b>
              <span className="text-slate-300">{o.customer_name}</span>
              <span className="font-mono-num text-slate-400">{o.total_sqft} sqft</span>
              <span className="font-mono-num text-slate-400">auto {r2(o.auto_total)}</span>
              <span className="text-[10px] text-slate-500">by {o.requested_by}</span>
            </div>
            {o.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-2 items-center text-xs">
                <span className="truncate text-slate-300">{l.item_name} · {l.sqft} sqft · auto {r2(l.auto_rate)}/sqft</span>
                <span className="text-[10px] text-slate-500 font-mono-num">APPROVED ₹/SQFT</span>
                <input type="number" min="0" step="0.01" value={rates[o.order_id]?.[i] ?? ""}
                  onChange={(e) => setRates((p) => ({ ...p, [o.order_id]: p[o.order_id].map((v, j) => (j === i ? e.target.value : v)) }))}
                  className="h-8 w-20 bg-slate-800/60 border border-amber-500/40 rounded-sm px-2 font-mono-num text-amber-200"
                  data-testid={`approval-rate-${o.order_number}-${i}`} />
                <span className="font-mono-num text-amber-200 w-20 text-right">{r2((Number(rates[o.order_id]?.[i]) || 0) * l.sqft)}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-300 mr-auto">Total: <b className="font-mono-num text-amber-300" data-testid={`approval-total-${o.order_number}`}>{r2(total)}</b></span>
              <button type="button" disabled={busy === o.order_id} onClick={() => act(o, false)} className="h-9 px-3 border border-red-500/60 text-red-300 rounded-sm text-xs font-mono-num" data-testid={`approval-reject-${o.order_number}`}>REJECT</button>
              <button type="button" disabled={busy === o.order_id} onClick={() => act(o, true)} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-xs font-heading tracking-widest" data-testid={`approval-give-${o.order_number}`}>GIVE PERMISSION</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
